"use client";

import { useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";
import { BlockRenderer } from "@/components/block-renderer";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { DietIcon } from "@/features/diets/components/diet-icon";
import type { SerializedTag } from "@/features/diets/helpers/serialize-tags";
import type { DietCatalog } from "@/features/diets/types";
import type { DietCatalogCarouselType } from "@/features/static-builder/types/diet-catalog-carousel.type";
import { Link } from "@/i18n/navigation";
import { cn } from "@/lib/utils";
import { DietCatalogCarouselSlider } from "./diet-catalog-carousel-slider";

export const DietCatalogCarouselWrapper = ({
  categories,
  data,
  diets,
  classWrapper,
  classCarouselWrapper,
  classCarouselContainerWrapper,
  showAllDietsButton,
}: {
  categories?: SerializedTag[];
  data?: DietCatalogCarouselType;
  diets: DietCatalog[];
  classWrapper?: string;
  classCarouselWrapper?: string;
  classCarouselContainerWrapper?: string;
  showAllDietsButton?: boolean;
}) => {
  const t = useTranslations();
  const listingCategories = useMemo(() => {
    if (!categories) {
      return [];
    }

    const availableSlugs = new Set(
      diets.flatMap((diet) => diet.tags.map((tag) => tag.slug)),
    );

    return categories.filter((category) => availableSlugs.has(category.slug));
  }, [categories, diets]);

  const [activeCategory, setActiveCategory] = useState<string>(
    listingCategories.at(0)?.slug ?? "",
  );

  const filteredDiets = useMemo(() => {
    if (!activeCategory) {
      return diets;
    }

    return diets.filter((diet) =>
      diet.tags.some((tag) => tag.slug === activeCategory),
    );
  }, [activeCategory, diets]);

  useEffect(() => {
    if (!activeCategory && listingCategories.length > 0) {
      const defaultCategory = listingCategories.at(0);
      if (defaultCategory) {
        setActiveCategory(defaultCategory.slug);
      }
    }
  }, [listingCategories, activeCategory]);

  return (
    <section
      className={cn(
        "md:container max-md:bg-brand-grey4 max-md:py-10 md:my-20",
        classWrapper,
      )}
    >
      <div
        className={cn(
          "rounded-xl max-md:container md:bg-brand-grey4 lg:flex",
          classCarouselContainerWrapper,
        )}
      >
        {data ? <Header data={data} /> : null}

        <div
          className={cn(
            "w-full md:p-6 lg:w-[65.5%] lg:p-10",
            classCarouselWrapper,
          )}
        >
          {categories ? (
            <Carousel
              className="mb-6 lg:mb-8"
              opts={{ align: "start", dragFree: true }}
            >
              <CarouselContent>
                {listingCategories.map((category) => (
                  <CarouselItem key={category.slug} className="basis-auto p-0">
                    <Button
                      className={cn(
                        "h-auto gap-2 rounded-none border-b-2 px-4 py-2.5 font-medium text-brand-grey5 text-lg leading-6 hover:bg-transparent hover:text-brand-green2-300",
                        activeCategory === category.slug
                          ? "border-brand-green2-300 text-brand-green2-300"
                          : "border-brand-grey2",
                      )}
                      onClick={() => {
                        setActiveCategory(category.slug);
                      }}
                      type="button"
                      variant="ghost"
                    >
                      <DietIcon className="h-6 w-6" tag={category.slug} />
                      <span className="whitespace-nowrap">
                        {category.label}
                      </span>
                    </Button>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          ) : null}

          <DietCatalogCarouselSlider
            key={activeCategory || "all-diets"}
            diets={filteredDiets}
          />

          {showAllDietsButton && (
            <Link
              className={cn(
                buttonVariants({ variant: "secondary", size: "lg" }),
                "mt-6 flex h-14 w-full rounded-xl px-5 lg:hidden",
              )}
              href="/diets"
            >
              {t("common.allDiets")}
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};

const Header = ({ data }: { data: DietCatalogCarouselType }) => {
  const t = useTranslations();
  return (
    <div className="hidden w-[34.5%] flex-col justify-center rounded-l-xl bg-brand-green2-300 px-6 py-10 text-brand-white lg:flex xl:px-16">
      <h2 className="font-medium text-5xl leading-14">{data.heading}</h2>
      <div className="mt-4 text-base leading-6">
        <BlockRenderer content={data.description} />
      </div>
      <Link
        className={cn(
          buttonVariants({ variant: "secondary", size: "lg" }),
          "mt-7 h-14 rounded-xl px-5",
        )}
        href="/diets"
      >
        {t("common.allDiets")}
      </Link>
    </div>
  );
};
